import axios from "@app/Security/AxiosAuthentication";
import Cookies from 'js-cookie';
import UserModel from "@app/Model/UserModel";
import UserInterface from "@app/Interface/UserInterface";
import UserViewPermissionsInterface from "@app/Interface/UserViewPermissionInterface";
import ProducerModel from "@app/Model/ProducerModel";
import UserViewPermissionPageInterface from "@app/Interface/UserViewPermissionPageInterface";
import ApiEndpoints from "@app/utils/ApiEndpoints";
import TemplateDefinitions from "@app/utils/TemplateDefinitions";
import UserFpuRole from "@app/Interface/UserFpuRoles";
import UserChartNotificationSettingsInterface from "@app/Interface/UserChartNotificationSettingsInterface";
import AppSettings from "@app/utils/AppSettings";
import { AxiosRequestConfig } from "axios";
import RollbarHelper from "@app/utils/RollbarHelper";
import QcSmartNotificationSettingInterface from "@app/Interface/QcSmartNotificationSettingsInterface";
import QcSmartNotificationSettingModel from "@app/Model/QcSmartNotificationSettingModel";
import QcSmartNotificationSettingPostInterface from "@app/Interface/QcSmartNotificationPostInterface";
import QcSmartNotificationSettingPostModel from "@app/Model/QcSmartNotificationPostModel";
import ApiCache from "@app/Service/ApiCache";

export default class UserAuthentication {

    static RESET_PASSWORD_ENDPOINT = "/api/reset_password";

    static REPORT_TEMPLATE_NAME = "report_template.php";

    static CHART_TEMPLATE_NAME = "chart_template.php";

    static LOCAL_STORAGE_NAVIGATION_KEY = "NAVIGATION_DATA";

    static LOCAL_STORAGE_USER_KEY = "USER_DATA";

    static LOCAL_STORAGE_NOTIFICATION_DATA = "NOTIFICATION_DATA";

    static LOCAL_STORAGE_PAGE_ROUTES_DATA = "PAGE_ROUTES_DATA";

    /**
     * Authenticate to Middleware, if good, save user information to cookie by default
     * Turn off saveCookies to verify username and password
     * 
     * @param username 
     * @param password 
     * @param saveCookies
     * @returns Promise<UserModel>
     */
    static signIn = async (username: string, password: string, saveCookies = true, twoFactorExpireDate?: string | undefined): Promise<UserModel | boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.LOGIN;

        // remove leading/trailing spaces from username
        username = username.trim();

        const data = {
            email: username,
            password: password,
            twoFaToken: twoFactorExpireDate
        };

        const options = {
            withCredentials: true
        };

        const response = await axios.post(url, data, options);

        // even though symfony is set up to return 200, it returns 302 instead.
        if (response.status === 302 || response.status === 202) {

            // redirect to code validation page
            throw new Error("2FA");

        } else if (response.status === 204) {

            const controller = new AbortController();

            const user = await UserAuthentication.getUser(controller, response.headers['location']);

            if (user.id) {

                if (saveCookies) {

                    Cookies.set("user-authenticated", "true", { expires: AppSettings.getCookieLifeTime() });

                    Cookies.set("user-iri", response.headers['location'], { expires: AppSettings.getCookieLifeTime() });

                }

                return user;  //SUCCESS!!!

            } else {

                throw new Error("Invalid Credentials");

            }

        }

        return false;

    }

    /**
     * Gets user data
     * 
     * @param controller AbortController - handles if component unmounts or if cancel button is added 
     * @param iri Appended to baseURL for getting a user
     * @returns Promise<UserModel>
     */
    static getUser = async (controller: AbortController, iri: string): Promise<UserModel> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const options: AxiosRequestConfig<never> = {
            withCredentials: true,
            signal: controller.signal
        };

        try {

            const response = await axios.get(baseUrl + iri, options);

            if (response.status === 200) {

                const user = UserAuthentication.getUserFromResponseData(response.data);

                return user;

            } else {

                RollbarHelper.logRollbarInfo(response, "getUser() Failed");

                const user = new UserModel();

                return user;

            }

        } catch (error) {

            RollbarHelper.logRequestError(error, "geUser()");

            const user = new UserModel();

            return user;

        }

    }

    /**
     * Converts string to json data for a user
     * TODO: We could refactor the UserModel class to handle converting the data. 
     *       using optional raw json data as a parameter. 
     * 
     * @param userData 
     * @returns 
     */
    static getUserFromResponseData = (userData: any): UserInterface => {

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let decodedFpu: any | null = null;

        if (userData.defaultFpuObject) {
            decodedFpu = JSON.parse(userData.defaultFpuObject);
        }

        const defaultProducer = new ProducerModel();

        if (decodedFpu) {
            defaultProducer.id = decodedFpu?.id;
            defaultProducer.rcdId = decodedFpu?.RcdID;
            defaultProducer.fieldRepId = decodedFpu?.FieldRepID;
            defaultProducer.producerId = decodedFpu?.ProducerID;
            defaultProducer.alphaName = decodedFpu?.AlphaName;
            defaultProducer.name1 = decodedFpu?.Name1;
            defaultProducer.name2 = decodedFpu?.Name2;
            defaultProducer.name3 = decodedFpu?.Name3;
            defaultProducer.name4 = decodedFpu?.Name4;
            defaultProducer.name5 = decodedFpu?.Name5;
            defaultProducer.startDate = decodedFpu?.StartDate;
            defaultProducer.quitDate = decodedFpu?.QuitDate;
            defaultProducer.ssn = decodedFpu?.SSN;
            defaultProducer.pdrType = decodedFpu?.PdrType;
            defaultProducer.fedOrd = decodedFpu?.FedOrd;
            defaultProducer.state = decodedFpu?.State;
            defaultProducer.county = decodedFpu?.County;
            defaultProducer.btu = decodedFpu?.BTU;
            defaultProducer.zone = decodedFpu?.Zone;
            defaultProducer.hauler = decodedFpu?.Hauler;
            defaultProducer.haulZone = decodedFpu?.HaulZone;
            defaultProducer.nbrTanks = decodedFpu?.NbrTanks;
            defaultProducer.voteDist = decodedFpu?.VoteDist;
            defaultProducer.probCode = decodedFpu?.ProbCode;
            defaultProducer.probStartDate = decodedFpu?.ProbStartDate;
            defaultProducer.oldPdr = decodedFpu?.OldPdr;
            defaultProducer.phone = decodedFpu?.Phone;
            defaultProducer.nonNda = decodedFpu?.NonNDA;
            defaultProducer.taxId = decodedFpu?.TaxId;
            defaultProducer.taxLegalName = decodedFpu?.TaxLegalName;

        }

        const user = new UserModel();
        user.id = userData.id;
        user.iri = userData["@id"];
        user.email = userData.email;
        user.username = userData.username;
        user.nickname = userData.nickname;
        user.firstName = userData.firstName;
        user.lastName = userData.lastName;
        user.contactName = userData.contactName;
        user.phoneNumber = userData.phoneNumber;
        user.cellNumber = userData.cellNumber;
        user.address = userData.address;
        user.address2 = userData.address2;
        user.city = userData.city;
        user.state = userData.state;
        user.zip = userData.zip;
        user.systemRoles = userData.systemRoles == "false" ? false : userData.systemRoles;
        user.fpuRoles = userData.fpuRoles !== 'false' ? UserAuthentication.getFpuRolesJson(userData.fpuRoles) : false;
        user.viewPermissions = UserAuthentication.getViewPermissionsJson(userData.viewPermissions);
        user.isMember = UserAuthentication.userIsMember(user.viewPermissions);
        user.currentFpuId = userData.defaultFpuId;
        user.currentFpu = defaultProducer;
        user.defaultFpuId = userData.defaultFpuId;
        user.enableTwoFactor = userData.enableTwoFactor;
        user.twoFactorType = userData.twoFactorType;
        user.twoFactorEmailAddress = userData.twoFactorEmailAddress ?? "";
        user.twoFactorPhoneNumber = userData.twoFactorPhoneNumber ?? "";
        user.confirmed = userData.confirmed ? true : false;
        user.enableNotifications = userData.enableNotifications;
        user.sendEmailNotifications = userData.sendEmailNotifications;
        user.notificationEmailAddress = userData.notificationEmailAddress ?? "";
        user.sendSmsNotifications = userData.sendSmsNotifications;
        user.notificationPhoneNumber = userData.notificationPhoneNumber ?? "";
        user.fpuChartNotificationSettings = userData.fpuChartNotificationSettings !== "false" ? UserAuthentication.getChartSettingsJson(userData.fpuChartNotificationSettings) : false;
        user.qcSmartNotificationSettings = userData.qcSmartNotificationSettings !== "false" ? UserAuthentication.getQcSmartSettingsJson(userData.qcSmartNotificationSettings) : false;
        return user;

    }

    /**
     * Gets user data
     * 
     * @param controller AbortController - handles if component unmounts or if cancel button is added 
     * @param fpuId Producer ID to filter users by 
     * @returns Promise<UserInterface>
     */
    static getUsers = async (controller: AbortController, fpuId: number): Promise<UserInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.USER_SAVE + `?fpu_id=${fpuId}`;

        const url = baseUrl + endPoint;

        return axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const dataItems: UserInterface[] = response.data["hydra:member"];

                const users: UserInterface[] = [];

                if (dataItems) {
                    dataItems.forEach(dataItem => {

                        users.push(UserAuthentication.getUserFromResponseData(dataItem));

                    });
                }

                return users;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getUsers()");

                return [];

            });

    }

    /**
     * finds matching user by email address 
     * @param controller AbortController - handles if component unmounts or if cancel button is added
     * @param email 
     * @returns 
     */
    static getUserByEmailAddress = async (controller: AbortController, email: string): Promise<UserInterface> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.USER_SAVE + `?search=${encodeURIComponent(email)}`;

        const url = baseUrl + endPoint;

        return axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const dataItems: UserInterface[] = response.data["hydra:member"];

                const users: UserInterface[] = [];

                if (dataItems) {
                    dataItems.forEach(dataItem => {

                        users.push(UserAuthentication.getUserFromResponseData(dataItem));

                    });
                }

                // check username OR email address
                let user = users.find(userToFind => String(userToFind.email).toLowerCase() === email.toLowerCase() || String(userToFind.username).toLocaleLowerCase() === email.toLocaleLowerCase());

                if (!user) {
                    user = new UserModel();
                }

                return user;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getUserByEmailAddress()");

                const user = new UserModel();

                return user;

            });

    }

    /**
     * Converts string view permissions into object
     * 
     * @param viewPermissions 
     * @returns 
     */
    static getViewPermissionsJson(viewPermissions: string): UserViewPermissionsInterface {

        let permissions: UserViewPermissionsInterface = JSON.parse("{}");

        if (viewPermissions !== "false") {
            permissions = JSON.parse(viewPermissions);
        }

        return permissions;
    }

    /**
     * convers string of fpu roles to json
     * 
     * @param fpuRoles 
     * @returns 
     */
    static getFpuRolesJson = (fpuRoles: string): UserFpuRole[] => {

        const roles: UserFpuRole[] = JSON.parse(fpuRoles);

        return roles;

    }

    /**
     * converts string of chart settings to json object
     * converts properties string types to correct type (number | boolean)
     * 
     * @param chartSettings 
     * @returns 
     */
    static getChartSettingsJson = (chartSettings: string): UserChartNotificationSettingsInterface[] => {

        const settings: UserChartNotificationSettingsInterface[] = JSON.parse(chartSettings);

        //convert string types 
        settings.forEach(setting => {

            setting.fpu = Number(setting.fpu);
            setting.active = setting.active ? true : false;
            setting.threshold_value_min = Number(setting.threshold_value_min);
            setting.threshold_value_max = Number(setting.threshold_value_max);

        });

        return settings;

    }

    /**
     * Converts string value of setting to json object. 
     * converts string properties to correct types. 
     * 
     * @param {string} data String value of the qc smart settings 
     * @returns 
     */
    static getQcSmartSettingsJson = (data: string): QcSmartNotificationSettingInterface[] => {

        const settings: QcSmartNotificationSettingInterface[] = JSON.parse(data);

        // convert strings to types
        settings.forEach(setting => {
            setting.fpu = Number(setting.fpu);
            setting.enable_qc_notification = String(setting.enable_qc_notification) === "true";
            setting.enable_smart_notification = String(setting.enable_smart_notification) === "true";
        });

        return settings;

    }

    /**
     * Determines if the user is logged in. 
     * @returns boolean
     */
    static userLoggedIn(): boolean {

        let blnSignedIn = false;

        const authenticated = Cookies.get("user-authenticated")?.toLowerCase();

        if (authenticated && authenticated === "true") {
            blnSignedIn = true;
        }

        return blnSignedIn;

    }

    /**
     * Returns the user iri from cookie data
     * @returns string IRI
     */
    static getUserLoggedInIri(): string {

        const iri = Cookies.get("user-iri")?.toLowerCase() ?? "";

        return iri;

    }

    /**
     * Gets the user data from the cookie
     * @returns UserModel
     */
    static getUserCookie(): UserModel {

        const userData = Cookies.get("user-data");

        let data: UserModel;

        const user = new UserModel();

        if (userData) {

            data = JSON.parse(userData);

            user.iri = data.iri;
            user.email = data.email;
            user.username = data.username;
            user.nickname = data.nickname;
            user.firstName = data.firstName;
            user.lastName = data.lastName;
            user.contactName = data.contactName;
            user.phoneNumber = data.phoneNumber;
            user.cellNumber = data.cellNumber;
            user.address = data.address;
            user.address2 = data.address2;
            user.city = data.city;
            user.state = data.state;
            user.zip = data.zip;
            user.systemRoles = data.systemRoles;
            user.fpuRoles = data.fpuRoles;
            user.viewPermissions = data.viewPermissions;
            user.currentFpuId = data.currentFpuId;

        }

        return user;

    }

    /**
     * Determines if logged in user can view the page.
     * 
     * @param user   Currently logged in user
     * @param fpuId  currenty fpu being viewed (not needed for system roles)
     * @param pageId current page being viewed
     * @returns 
     */
    static canUserViewPage(user: UserInterface, fpuId: number, pageId: number): boolean {

        let canView = false;

        if (user.viewPermissions) {

            // check system roles first
            canView = UserAuthentication.canSystemUserViewPage(user.viewPermissions, pageId);

            if (canView === false) {

                // check fpu roles 
                canView = UserAuthentication.canFpuUserViewPage(user.viewPermissions, fpuId, pageId);

            }

        }

        return canView;

    }

    /**
     * Determines if viewer has system level permissions 
     * to view the page. current FPU is not used
     * 
     * @param viewPermissions Permissions of logged in user
     * @param pageId          Current page being viewed
     * @returns 
     */
    static canSystemUserViewPage(viewPermissions: UserViewPermissionsInterface, pageId: number): boolean {

        let canView = false;

        if (viewPermissions.by_system) {

            viewPermissions.by_system.posts.forEach(post => {

                if (post.post_id === pageId) {
                    canView = true;
                }

            });
        }

        return canView;
    }

    /**
     * Determines if logged in user can view page based on
     * FPU user permissions
     * 
     * @param viewPermissions Permissions of logged in user
     * @param fpuId           Current Fpu being viewed 
     * @param pageId          Current page being viewed
     * @returns 
     */
    static canFpuUserViewPage(viewPermissions: UserViewPermissionsInterface, fpuId: number, pageId: number): boolean {

        let canView = false;

        if (viewPermissions.by_fpu) {

            viewPermissions.by_fpu.forEach(fpuPermission => {

                if (Number(fpuPermission.fpu_id) == fpuId) {

                    fpuPermission.posts.forEach(post => {

                        if (post.post_id === pageId) {
                            canView = true;
                        }

                    });

                }


            });

        }

        return canView;

    }

    /**
     * Logs the user out of the frontend 
     * and middleware
     * @returns boolean
     */
    static logout = async (waitForResponse?: boolean): Promise<boolean> => {

        let loggedOut = false;

        if (waitForResponse) {

            loggedOut = await UserAuthentication.logoutMiddleware();

        } else {

            loggedOut = true;

            UserAuthentication.logoutMiddleware();

        }

        if (loggedOut) {

            Cookies.remove("user-authenticated", { path: "/" });

            Cookies.remove("user-iri", { path: "/" });

            localStorage.removeItem(UserAuthentication.LOCAL_STORAGE_NAVIGATION_KEY);

            localStorage.removeItem(UserAuthentication.LOCAL_STORAGE_USER_KEY);

            localStorage.removeItem(UserAuthentication.LOCAL_STORAGE_NOTIFICATION_DATA);

            localStorage.removeItem(UserAuthentication.LOCAL_STORAGE_PAGE_ROUTES_DATA);

            ApiCache.deleteNavigationData();

        }

        return loggedOut;

    }

    /**
     * Logs the user out from the middleware
     * @returns boolean
     */
    static logoutMiddleware = async (): Promise<boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.LOGOUT;

        const options = {
            withCredentials: true
        };

        try {

            // currently stage middleware response is returning mixed Content
            // causing an error, so we'll just assume all went well.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const response = await axios.get(url, options);

            return true;

        } catch (error) {

            RollbarHelper.logRequestError(error, "logoutMiddleware()");

            return true;
        }

    }

    /*
     * Save user to Middleware endpoint
     * @param userData 
     * @returns 
     */
    static saveUser = async (userData: UserModel): Promise<number | boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.USER_SAVE + "/" + userData.id;

        const data = {
            email: userData.email.trim(),
            nickname: userData.nickname,
            firstName: userData.firstName,
            lastName: userData.lastName,
            contactName: userData.contactName,
            phoneNumber: userData.phoneNumber,
            cellNumber: userData.cellNumber,
            address: userData.address,
            address2: userData.address2,
            city: userData.city,
            state: userData.state,
            zip: userData.zip,
            enableTwoFactor: userData.enableTwoFactor,
            twoFactorType: userData.twoFactorType,
            twoFactorEmailAddress: userData.twoFactorEmailAddress,
            twoFactorPhoneNumber: userData.twoFactorPhoneNumber,
            enableNotifications: userData.enableNotifications,
            notificationEmailAddress: userData.notificationEmailAddress,
            notificationPhoneNumber: userData.notificationPhoneNumber,
            sendEmailNotifications: userData.sendEmailNotifications,
            sendSmsNotifications: userData.sendSmsNotifications,
        };

        return axios.patch(url, data, {
            withCredentials: true,
            headers: {
                "Content-Type": "application/merge-patch+json",
            }
        })
            .then(response => {

                return response.status;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "saveUser()");

                return false;

            });

    }

    /**
     * Updates the user's password
     * 
     * @param userData 
     * @param password 
     * @returns 
     */
    static saveUserPassword = async (userData: UserModel, password: string | number | undefined, jwt?: string): Promise<number | boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.USER_SAVE + "/" + userData.id + "/reset_password";

        let data: { jwt?: string; password: string | number | undefined; };

        if (jwt) {

            data = {
                jwt: jwt,
                password: password
            }

        } else {

            data = {
                password: password
            };

        }

        return axios.post(url, data, {
            withCredentials: true
        })
            .then(response => {

                return response.status;

            })
            .catch(error => {

                // TODO: Does this send password data to rollbar?
                // RollbarHelper.logRequestError(error, "saveUserPassword()");

                console.info("url: ", url);

                console.info("saveUserPassword get error: ", error);

                return false;

            });

    }


    /**
     * Endpoint send user a link to update their password.
     * 
     * @param userId 
     * @param email 
     * @returns 
     */
    static resetUserPassword = async (email: string): Promise<boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.RESET_PASSWORD;

        const data = {
            emailAddress: email.trim()
        };

        return axios.post(url, data, {
            withCredentials: true
        })
            .then(response => {

                if (response.status === 201 || response.status === 200) {
                    return true;
                } else {
                    return false;
                }
            })
            .catch(error => {

                // TODO: Does this send password data to rollbar?
                // RollbarHelper.logRequestError(error, "resetUserPassword()");

                console.info("url: ", url);

                console.info("resetUserPassword get error: ", error);

                return false;

            });
    }

    /**
     * Non-async call to save QC Smart Settings to middleware.
     * 
     * @param {number}                                userId  User Id that will get updated
     * @param {QcSmartNotificationSettingInterface[]} optins    The user's optins for notificationEmailAddress
     * @returns 
     */
    static saveUserQcSmartSettingsNonAsync = (userId: number, optin: QcSmartNotificationSettingInterface): boolean => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.USER_UPDATE_QC_SMART_SETTINGS.replace("{id}", String(userId));

        const config = {
            withCredentials: true
        };

        let success = false;

        const qcSmartSetting = new QcSmartNotificationSettingPostModel();
        qcSmartSetting.fpuId = optin.fpu;
        qcSmartSetting.enableQc = optin.enable_qc_notification;
        qcSmartSetting.enableSmart = optin.enable_smart_notification;

        try {

            axios.post(url, qcSmartSetting, config);

            success = true;

        } catch (error) {

            // log error

        }

        return success;

    }

    /**
     * We have to convert the user data into the post data format used by the endpoint
     * 
     * @param {QcSmartNotificationSettingInterface[]} optins An array of optins to convert
     * 
     * @returns 
     */
    static convertOptinsDataToPostData = (optins: QcSmartNotificationSettingInterface[]): QcSmartNotificationSettingPostInterface[] => {

        const settings: QcSmartNotificationSettingPostInterface[] = [];

        optins.forEach(optin => {

            const setting = new QcSmartNotificationSettingPostModel();

            setting.fpuId = optin.fpu;

            setting.enableQc = optin.enable_qc_notification;

            setting.enableSmart = optin.enable_smart_notification;

            settings.push(setting);

        });

        return settings;

    }

    /**
     * Endpoint send user a link to update their password.
     * 
     * @param controller AbortController - Handles if component is unmounted or cancel button is added
     * @param userId 
     * @param email 
     * @returns 
     */
    static validateJwt = async (controller: AbortController, userId: number, jwt: string): Promise<boolean> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.VALIDATE_JWT;

        const data = {
            userId: userId,
            jwt: jwt
        };

        return axios.post(url, data, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                if (response.status === 200) {
                    return true;
                } else {
                    return false;
                }
            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "validateJwt()");

                return false;

            });
    }

    /**
     * Determines if logged in user can view a dashboard import 
     * based on view permissions. 
     * - Check system roles first
     * - Then check the current fpu role
     * - return 0 if they don't have permission to view dashboard item
     *  
     * @param viewPermissions Permissions of logged in user
     * @param fpuId           Current Fpu being viewed 
     * @param pageId          Current page being viewed
     * @param templateName    Name of template to look for 
     * @param reportType      Optional Report type to look for
     * @param chartType       Optional Chart type to look for
     * @returns 
     */
    static canUserViewDashboardItem(viewPermissions: UserViewPermissionsInterface, fpuId: number, templateName: string, reportType?: string, chartType?: string): boolean {

        let canView = false;

        // check system roles first
        if (viewPermissions.by_system.posts && viewPermissions.by_system.posts.length > 0) {

            canView = UserAuthentication.canUserViewTemplate(viewPermissions.by_system.posts, templateName, reportType, chartType);

        }

        // only check fpu roles if we haven't found the page yet
        if (!canView && viewPermissions.by_fpu) {

            viewPermissions.by_fpu.forEach(fpuPermission => {

                if (Number(fpuPermission.fpu_id) === fpuId) {

                    canView = UserAuthentication.canUserViewTemplate(fpuPermission.posts, templateName, reportType, chartType);

                }

            });

        }

        return canView;

    }

    /**
     * Determines if user has permission to view a page based on template name, report type, and chart type.
     * 
     * @param posts 
     * @param templateName 
     * @param reportType 
     * @param chartType 
     * @returns 
     */
    static canUserViewTemplate = (posts: UserViewPermissionPageInterface[], templateName: string, reportType?: string, chartType?: string): boolean => {

        let view = false;

        const isReport = templateName === TemplateDefinitions.TEMPLATE_REPORT_PAGE;

        const isChart = templateName === TemplateDefinitions.TEMPLATE_CHART_PAGE;

        // array.some() 
        // https://developer.mozilla.org/en-US/docs/web/javascript/reference/global_objects/array/some
        view = posts.some((post) => {

            let view = false;

            // post is a WordPress page object
            if (isReport && post.report_type === reportType) {
                view = true;
            } else if (isChart && post.chart_type === chartType) {
                view = true;
            } else if (!isReport && !isChart && post.template === templateName) {
                view = true;
            }

            return view;

        });

        return view;

    }

    /**
     * Determines if a user is a member. 
     * If Member, they see theme for NDA
     * Non-member sees Lab theme
     * 
     * @param viewPermissions 
     * @returns 
     */
    static userIsMember = (viewPermissions: UserViewPermissionsInterface): boolean => {

        let isMember = false;


        if (viewPermissions.by_system && viewPermissions.by_system.has_member_account_role) {

            isMember = true;

        }

        if (!isMember && viewPermissions.by_fpu) {

            isMember = viewPermissions.by_fpu.some((permission) => { return permission.has_member_account_role; });

        }

        return isMember;

    }

    /**
     * Determines if user can has a master account role
     * 
     * @param viewPermissions 
     * @param producerId 
     * @returns 
     */
    static userHasMasterAccountRole = (viewPermissions: UserViewPermissionsInterface, producerId: number): boolean => {

        let hasMasterAccount = false;

        if (viewPermissions.by_system && viewPermissions.by_system.has_master_account_role) {
            hasMasterAccount = true;
        }

        if (!hasMasterAccount && viewPermissions.by_fpu) {
            hasMasterAccount = viewPermissions.by_fpu.some((permissions) => { return permissions.has_master_account_role && Number(permissions.fpu_id) === producerId; });
        }

        return hasMasterAccount;

    }

    /**
     * Determines is a user role has permission to opt into QC notification
     * 
     * @param {UserViewPermissionsInterface} viewPermissions permissions assigned to user
     * @param {number}                       producerId      ProducerID currently viewed by user.
     * @returns 
     */
    static userCanReceiveQcNotification = (viewPermissions: UserViewPermissionsInterface, producerId: number): boolean => {

        let canReceive = false;

        if (viewPermissions.by_system && viewPermissions.by_system.gets_qc_notification) {
            canReceive = true;
        }

        if (!canReceive && viewPermissions.by_fpu) {
            canReceive = viewPermissions.by_fpu.some(permissions => { return Number(permissions.fpu_id) === producerId && permissions.gets_qc_notification });
        }

        return canReceive;

    }

    /**
     * Determines is a user role has permission to opt into Smart notification
     * 
     * @param {UserViewPermissionsInterface} viewPermissions permissions assigned to user
     * @param {number}                       producerId      ProducerID currently viewed by user.
     * @returns 
     */
    static userCanReceiveSmartNotification = (viewPermissions: UserViewPermissionsInterface, producerId: number): boolean => {

        let canReceive = false;

        if (viewPermissions.by_system && viewPermissions.by_system.gets_qc_notification) {
            canReceive = true;
        }

        if (!canReceive && viewPermissions.by_fpu) {
            canReceive = viewPermissions.by_fpu.some(permissions => { return Number(permissions.fpu_id) === producerId && permissions.gets_smart_notification });
        }

        return canReceive;

    }

    /**
     * tries to find an existing QcSmartNotificationSetting for the producer id. 
     * if not found, returns an empty setting with false values
     * 
     * @param settings 
     * @param producerId 
     * @returns 
     */
    static getQcSmartSettingByFpu = (settings: QcSmartNotificationSettingInterface[], producerId: number): QcSmartNotificationSettingInterface => {

        let setting = settings.find(setting => setting.fpu === producerId);

        if (!setting) {
            setting = new QcSmartNotificationSettingModel();
        }

        return setting;

    }

    /**
     * Determines if user has permission to grant access for an FPU
     * 
     * @param viewPermissions 
     * @param producerId 
     * @returns 
     */
    static userCanGrantAccess = (viewPermissions: UserViewPermissionsInterface, producerId: number): boolean => {

        let canGrant = false;

        if (viewPermissions.by_system && viewPermissions.by_system.can_grant_access) {
            canGrant = true;
        }

        if (!canGrant && viewPermissions.by_fpu) {
            canGrant = viewPermissions.by_fpu.some((permissions) => { return permissions.can_grant_access && Number(permissions.fpu_id) === producerId; });
        }

        return canGrant;

    }

    /**
     * Determines if user has access to a particular Fpu
     * 
     * @param viewPermissions 
     * @param producerId 
     * @returns 
     */
    static userHasFpu = (viewPermissions: UserViewPermissionsInterface, producerId: number): boolean => {

        let hasFpu = false;

        if (viewPermissions.by_fpu) {
            hasFpu = viewPermissions.by_fpu.some((permissions) => { return Number(permissions.fpu_id) === producerId; });
        }

        return hasFpu;

    }

}
